import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CartSheet({ onCheckout }) {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, count } = useCart();

  const handleCheckout = () => {
    setIsOpen(false);
    setTimeout(() => onCheckout && onCheckout(), 150);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-stone-50 flex flex-col"
        data-testid="cart-sheet"
      >
        <SheetHeader className="border-b border-stone-200 pb-4">
          <SheetTitle className="font-serif text-2xl text-stone-900">
            Your Basket
          </SheetTitle>
          <SheetDescription className="text-stone-500 text-sm">
            {count === 0
              ? "Your basket is empty."
              : `${count} item${count > 1 ? "s" : ""} ready for delivery.`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          {items.length === 0 ? (
            <div className="h-full grid place-items-center text-center text-stone-500 py-16">
              <div>
                <ShoppingBag className="w-10 h-10 mx-auto text-stone-300" />
                <p className="mt-4 text-sm">
                  Add a jar to get started —<br /> your village kitchen awaits.
                </p>
              </div>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((i) => (
                <li
                  key={i.id}
                  className="flex gap-3 bg-white border border-stone-200 rounded-2xl p-3"
                  data-testid={`cart-item-${i.id}`}
                >
                  <img
                    src={i.image}
                    alt={i.name}
                    className="w-20 h-20 rounded-xl object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-base text-stone-900 truncate">
                      {i.name}
                    </div>
                    <div className="text-sm text-[#993311] font-semibold mt-0.5">
                      ₹{i.price}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center border border-stone-300 rounded-full">
                        <button
                          onClick={() => updateQty(i.id, i.quantity - 1)}
                          className="w-7 h-7 grid place-items-center text-stone-700"
                          data-testid={`cart-qty-decrease-${i.id}`}
                          aria-label="Decrease"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-7 text-center text-sm tabular-nums">
                          {i.quantity}
                        </span>
                        <button
                          onClick={() => updateQty(i.id, i.quantity + 1)}
                          className="w-7 h-7 grid place-items-center text-stone-700"
                          data-testid={`cart-qty-increase-${i.id}`}
                          aria-label="Increase"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(i.id)}
                        className="text-stone-400 hover:text-[#993311]"
                        data-testid={`cart-remove-${i.id}`}
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-stone-200 pt-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-stone-600">Subtotal</span>
              <span className="font-serif text-2xl text-stone-900" data-testid="cart-subtotal">
                ₹{subtotal.toFixed(0)}
              </span>
            </div>
            <p className="text-xs text-stone-500">
              Delivery charges confirmed after we contact you on WhatsApp.
            </p>
            <Button
              onClick={handleCheckout}
              className="w-full bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full h-12"
              data-testid="cart-checkout-btn"
            >
              Proceed to Order
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
